<template>
  <el-dialog
    :visible.sync="innerVisible"
    width="560px"
    custom-class="ca-invoice-apply"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="!submitting"
    @closed="handleClosed"
    title="发票信息"
  >
    <div v-loading="detailLoading">
      <div class="ca-invoice-apply__order">
        <span class="ca-invoice-apply__order-label">订单号：</span>{{ order.orderCode }}
      </div>
      <div class="ca-invoice-apply__status">发票状态：{{ statusText }}</div>
      <el-form ref="form" :model="form" label-width="110px" class="ca-invoice-apply__form">
        <el-form-item label="发票抬头" prop="invoiceTitle" :rules="rules.invoiceTitle">
          <el-input v-model.trim="form.invoiceTitle" maxlength="255" placeholder="请输入发票抬头" />
        </el-form-item>
        <el-form-item label="纳税人识别号" prop="taxNumber" :rules="rules.taxNumber">
          <el-input v-model.trim="form.taxNumber" maxlength="18" placeholder="请输入纳税人识别号" />
        </el-form-item>
        <el-form-item label="发票类型" prop="invoiceType" :rules="rules.invoiceType">
          <el-radio-group v-model="form.invoiceType" @change="handleTypeChange">
            <el-radio :label="INVOICE_TYPE.GENERAL">增值税普通发票</el-radio>
            <el-radio :label="INVOICE_TYPE.SPECIAL">增值税专用发票</el-radio>
          </el-radio-group>
        </el-form-item>
        <!-- 专票专有字段：:required 仅控制红星显示（随专票/普票切换联动），必填校验由 rules 的 validator 承担 -->
        <el-form-item
          label="开票地址"
          prop="invoiceAddress"
          :rules="rules.invoiceAddress"
          :required="isSpecial"
        >
          <el-input
            v-model.trim="form.invoiceAddress"
            maxlength="200"
            placeholder="请输入开票地址"
          />
        </el-form-item>
        <el-form-item
          label="开票电话"
          prop="invoicePhone"
          :rules="rules.invoicePhone"
          :required="isSpecial"
        >
          <el-input v-model.trim="form.invoicePhone" maxlength="20" placeholder="请输入开票电话" />
        </el-form-item>
        <el-form-item
          label="开票户行"
          prop="invoiceBank"
          :rules="rules.invoiceBank"
          :required="isSpecial"
        >
          <el-input v-model.trim="form.invoiceBank" maxlength="255" placeholder="请输入开票户行" />
        </el-form-item>
        <el-form-item
          label="开票户行账号"
          prop="invoiceBankNo"
          :rules="rules.invoiceBankNo"
          :required="isSpecial"
        >
          <el-input
            v-model.trim="form.invoiceBankNo"
            maxlength="255"
            placeholder="请输入开票户行账号"
          />
        </el-form-item>
        <el-form-item label="开票备注" prop="invoiceRemark" :rules="rules.invoiceRemark">
          <el-input
            v-model="form.invoiceRemark"
            type="textarea"
            autosize
            maxlength="200"
            show-word-limit
            placeholder="请输入开票备注"
          />
        </el-form-item>
        <el-form-item label="接收发票邮箱" prop="invoiceEmail" :rules="rules.invoiceEmail">
          <el-input
            v-model.trim="form.invoiceEmail"
            maxlength="100"
            placeholder="请输入接收发票邮箱"
          />
        </el-form-item>
      </el-form>
    </div>
    <div slot="footer" class="ca-invoice-apply__footer">
      <el-button
        class="ca-invoice-apply__cancel"
        :disabled="submitting"
        @click="innerVisible = false"
      >
        取消
      </el-button>
      <el-button class="ca-btn--primary" :loading="submitting" @click="handleSubmit">
        确认
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { getInvoice, applyInvoice } from '@/api/ca'
import { INVOICE_TYPE, INVOICE_STATUS_TEXT } from '../util/caForm'

// 专票专有字段：切到普票时这些字段变选填，需清除残留校验态
const SPECIAL_ONLY_PROPS = ['invoiceAddress', 'invoicePhone', 'invoiceBank', 'invoiceBankNo']

const EMPTY_FORM = {
  invoiceType: INVOICE_TYPE.GENERAL,
  invoiceTitle: '',
  taxNumber: '',
  invoiceAddress: '',
  invoicePhone: '',
  invoiceBank: '',
  invoiceBankNo: '',
  invoiceRemark: '',
  invoiceEmail: '',
}

export default {
  name: 'InvoiceApplyDialog',
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, default: () => ({}) },
  },
  data() {
    // 纳税人识别号：18 位，仅数字或大写字母（不复用 shared/validate 的 15-20 位规则）
    const validateTaxNumber = (rule, value, callback) => {
      if (!value) return callback(new Error('请输入纳税人识别号'))
      if (!/^[0-9A-Z]{18}$/.test(value)) {
        return callback(new Error('纳税人识别号须为18位数字或大写字母'))
      }
      callback()
    }
    // 户行账号不允许空格
    const validateBankNo = (rule, value, callback) => {
      if (this.isSpecial && !value) return callback(new Error('请输入开票户行账号'))
      if (value && /\s/.test(value)) return callback(new Error('开票户行账号不能包含空格'))
      callback()
    }
    // 专票必填、普票选填的通用必填校验
    const specialRequired = (message) => (rule, value, callback) => {
      if (this.isSpecial && !value) return callback(new Error(message))
      callback()
    }
    return {
      INVOICE_TYPE,
      form: { ...EMPTY_FORM },
      detailLoading: false, // 打开时取详情预填中
      submitting: false, // 提交中（确认按钮 loading 防重复）
      invoiceStatus: '', // 详情下发的发票状态（头部红字展示）
      rules: {
        invoiceTitle: [
          { required: true, message: '请输入发票抬头', trigger: 'blur' },
          { max: 255, message: '长度不能超过255个字符', trigger: 'blur' },
        ],
        taxNumber: [{ required: true, validator: validateTaxNumber, trigger: 'blur' }],
        invoiceType: [{ required: true, message: '请选择发票类型', trigger: 'change' }],
        invoiceAddress: [
          { validator: specialRequired('请输入开票地址'), trigger: 'blur' },
          { max: 200, message: '长度不能超过200个字符', trigger: 'blur' },
        ],
        invoicePhone: [
          { validator: specialRequired('请输入开票电话'), trigger: 'blur' },
          { max: 20, message: '长度不能超过20个字符', trigger: 'blur' },
        ],
        invoiceBank: [
          { validator: specialRequired('请输入开票户行'), trigger: 'blur' },
          { max: 255, message: '长度不能超过255个字符', trigger: 'blur' },
        ],
        invoiceBankNo: [{ validator: validateBankNo, trigger: 'blur' }],
        invoiceRemark: [{ max: 1000, message: '长度不能超过1000个字符', trigger: 'blur' }],
        invoiceEmail: [
          { required: true, message: '请输入接收发票邮箱', trigger: 'blur' },
          {
            type: 'email',
            message: '请输入正确的邮箱',
            trigger: ['blur', 'change'],
          },
          { max: 100, message: '长度不能超过100个字符', trigger: 'blur' },
        ],
      },
    }
  },
  computed: {
    innerVisible: {
      get() {
        return this.visible
      },
      set(v) {
        this.$emit('update:visible', v)
      },
    },
    isSpecial() {
      return this.form.invoiceType === INVOICE_TYPE.SPECIAL
    },
    statusText() {
      return INVOICE_STATUS_TEXT[String(this.invoiceStatus)] || '待申请'
    },
  },
  watch: {
    visible(v) {
      if (v) this.loadDetail()
    },
  },
  methods: {
    // 打开时查询发票信息预填：已申请过（含失败重提）回填已提交数据；
    // 未申请时明细为空，用响应中的 applyOrgName / licenceCode 默认带入抬头/识别号
    loadDetail() {
      this.detailLoading = true
      getInvoice({ orderId: this.order.orderId })
        .then((res) => {
          const d = res.data || {}
          this.invoiceStatus = d.invoiceStatus
          this.form = {
            ...EMPTY_FORM,
            invoiceType: d.invoiceType || INVOICE_TYPE.GENERAL,
            invoiceTitle: d.invoiceTitle || d.applyOrgName || '',
            taxNumber: d.taxNumber || d.licenceCode || '',
            invoiceAddress: d.invoiceAddress || '',
            invoicePhone: d.invoicePhone || '',
            invoiceBank: d.invoiceBank || '',
            invoiceBankNo: d.invoiceBankNo || '',
            invoiceRemark: d.invoiceRemark || '',
            invoiceEmail: d.invoiceEmail || '',
          }
          this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate())
        })
        .catch(() => {
          // 加载失败关闭弹框（错误提示由请求拦截器统一处理）
          this.innerVisible = false
        })
        .finally(() => {
          this.detailLoading = false
        })
    },
    handleTypeChange() {
      // 切到普票后专票字段变选填，清除残留必填报错
      this.$refs.form && this.$refs.form.clearValidate(SPECIAL_ONLY_PROPS)
    },
    handleSubmit() {
      if (this.submitting) return
      this.$refs.form.validate((valid) => {
        if (!valid) return
        this.submitting = true
        applyInvoice({ orderId: this.order.orderId, ...this.form })
          .then(() => {
            this.$message.success('发票申请提交成功')
            this.$emit('success')
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },
    // 关闭（取消/右上角 X）后重置，不保留数据
    handleClosed() {
      this.form = { ...EMPTY_FORM }
      this.invoiceStatus = ''
      this.$refs.form && this.$refs.form.clearValidate()
    },
  },
}
</script>
